import { t } from 'i18next';

import store from '../store/store';
import type HttpError from '../utils/apis/HttpError';
import { type Logger } from '../apis/logger';
import { type Entity, LevelType } from '../apis/ilink.type';
import { getProductFromCatalog } from '../apis/masterdata/masterdata';
import { type BreadcrumbLevel, updateCatalogSearch } from '../store/catalogSlice';
import { initInterventionState, resetIntervention } from '../store/interventionSlice';
import { deleteCustomer } from '../store/customerSlice';
import { checkShippingGroup } from './shippingHelper';
import { type TranslationKey } from '../utils/i18n/types';
import { type Locale } from '../store/i18nSlice';
import { BACKUP_LOCALE } from '../constants/app';
import { getAppRoute, RoutesPath } from '../core/routes';

export const normalizedString = (param: string) =>
  param
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const capitalizeString = (word: string) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '');

export const handleError = (errorDetails?: string) => {
  const details = errorDetails ? ` ${errorDetails}` : '';
  throw `${t('error.generic.message')}${details}`;
};

type ErrorType = 'intervention' | 'customer' | 'member' | 'dictionary';
const errorCodesSet = new Set([504, 403]);

export const handleHttpErrors = (
  httpError: HttpError,
  errorType: ErrorType,
  logger: Logger,
  optionalParams: { [key: string]: string | number | boolean }
): Promise<any> => {
  if (httpError?.response && !errorCodesSet.has(httpError.response.status) && httpError.response.json) {
    return httpError.response
      .json()
      .catch(error => {
        void logger.log(error, optionalParams);
        throw t('error.generic.message');
      })
      .then((error: any) => {
        const errorToLog = new Error();
        errorToLog.stack = httpError.stack;
        if (errorType === 'intervention') {
          errorToLog.message = error.error ? `${error.status} ${error.error} : ${error.message}` : error.message || t('error.generic.message');
          void logger.log(errorToLog, {
            ...optionalParams,
            uri: optionalParams.url,
            correlationId: httpError.response?.headers.get('x-correlation-id'),
            document_number: optionalParams.docNumber,
          });
          throw errorToLog.message;
        } else if (error?.error && errorType) {
          const errorCode = `error.message.${errorType}.${error.error.code}` as TranslationKey;
          const message = t(errorCode);
          throw message === errorCode ? error.error.message : message;
        }
        void logger.log(httpError, optionalParams);
        return handleError();
      });
  }
  void logger.log(httpError, optionalParams);
  return new Promise(() => {
    handleError();
  });
};

export const handle404Errors = (httpError: HttpError, logger: Logger, url: string, defaultValue: unknown) => {
  if (httpError?.response?.status === 404) {
    return Promise.resolve(defaultValue);
  }
  return handleHttpErrors(httpError, 'intervention', logger, { url });
};

export const willUpdateEntity = (prevEntity: Entity, prevEntities: Entity[], nextEntity: Entity, nextEntities: Entity[]) =>
  prevEntity.quantity === nextEntity.quantity &&
  prevEntity.articles.length === nextEntity.articles.length &&
  prevEntity.articles.every((article, index) => article.selected === nextEntity.articles[index].selected) &&
  prevEntities?.length === nextEntities?.length &&
  (!prevEntities || prevEntities.every((entity, index) => entity.quantity === nextEntities[index].quantity)) &&
  (!prevEntities || prevEntities.every((entity, index) => entity.articles.length === nextEntities[index].articles.length)) &&
  (!prevEntities ||
    prevEntities.every((entity, index) =>
      entity.articles?.every((article, articlesIndex) => article.selected === nextEntities[index].articles[articlesIndex].selected)
    ));

export const formatUseLanguageCode = (appLanguage: Locale | null) => {
  if (appLanguage === null) {
    return BACKUP_LOCALE.replace('-', '_');
  }
  return `${appLanguage?.language.toLowerCase()}_${appLanguage?.country.toUpperCase()}`;
};

export const getUserLocale = () => {
  const appLanguage = store.getState().i18n.appLanguage;

  return formatUseLanguageCode(appLanguage);
};

export const homePageSearchAndGetRedirectPath = (
  searchedText: string,
  productSearchActive: boolean,
  addErrorToast: (message: string) => void
): Promise<{ pathname: string } | null> => {
  if (searchedText) {
    const isShippingGroup = checkShippingGroup(searchedText);

    if (!isShippingGroup && Number.isNaN(Number(searchedText))) {
      addErrorToast(t('error.message.invalid.searched.text'));
      return Promise.resolve(null);
    }

    return (!isShippingGroup && productSearchActive ? getProductFromCatalog(searchedText).catch(() => null) : Promise.resolve(null)).then(
      catalogProduct => {
        const state = store.getState();
        if (catalogProduct?.productInformation) {
          const { product, productInformation } = catalogProduct;
          const catalogSearch = state.catalog.search;
          const updatedBreadcrumb: BreadcrumbLevel[] = [
            { ...catalogSearch.breadcrumb[0], hide: true },
            { level: LevelType.categories, label: productInformation.process.name, id: productInformation.process.id, hide: true },
            { level: LevelType.products, label: productInformation.category.name, id: productInformation.category.id, hide: true },
            { level: LevelType.product, label: t('catalog.breadcrumb.search.product') },
          ];

          store.dispatch(
            updateCatalogSearch({
              ...catalogSearch,
              product,
              breadcrumb: updatedBreadcrumb,
              fetchNeeded: true,
            })
          );
          store.dispatch(initInterventionState());
          return { pathname: getAppRoute(RoutesPath.productPage, String(product.id)) };
        }

        if (isShippingGroup) {
          return { pathname: getAppRoute(RoutesPath.shippingGroupPage, searchedText) };
        }
        store.dispatch(resetIntervention(state.intervention.footer.docNumber === searchedText));
        store.dispatch(deleteCustomer());
        return { pathname: getAppRoute(RoutesPath.interventionPage, searchedText) };
      }
    );
  }

  return Promise.resolve(null);
};
